import type {
    SpreadsheetOptions,
    SpreadsheetCell,
    CellValue,
    CsvOptions,
} from './spreadsheet.js'

export const LEAK_RATE_HEADERS:string[] = [
    'Date',
    'Customer',
    'Equipment ID',
    'Refrigerant Type',
    'Added (lbs)',
    'Full Charge (lbs)',
    'Previous Service Date',
    'Days Since Last Addition',
    'Leak Rate %',
    'EPA Threshold %',
    'Compliance Status',
]

export const REFRIGERANT_LOG_HEADERS:string[] = [
    'Date',
    'Technician',
    'EPA Cert #',
    'Customer',
    'Site Name',
    'Equipment ID',
    'Refrigerant Type',
    'Added (lbs)',
    'Recovered (lbs)',
    'Full Charge (lbs)',
    'Work Performed',
    'Notes',
]

function leakRateRow (rowNum:number):CellValue[] {
    const r = rowNum
    return [
        null,           // Date (A)
        null,           // Customer (B)
        null,           // Equipment ID (C)
        null,           // Refrigerant Type (D)
        null,           // Added lbs (E)
        null,           // Full Charge lbs (F)
        null,           // Previous Service Date (G)
        {               // Days Since Last Addition (H)
            value: null,
            formula: `MAX(1,INT(A${r}-G${r}))`,
        } as SpreadsheetCell,
        {               // Leak Rate % (I)
            value: null,
            formula: `IF(AND(E${r}>0,F${r}>0,H${r}>0),(E${r}/F${r})*(365/H${r})*100,"")`,
        } as SpreadsheetCell,
        null,           // EPA Threshold % (J) — user fills this in
        {               // Compliance Status (K)
            value: null,
            formula: `IF(AND(I${r}<>"",J${r}<>""),IF(I${r}>J${r},"EXCEEDS","Compliant"),"")`,
        } as SpreadsheetCell,
    ]
}

export function buildLeakRateTrackingXlsx ():SpreadsheetOptions {
    const rows:CellValue[][] = []
    for (let i = 2; i <= 51; i++) {
        rows.push(leakRateRow(i))
    }
    return {
        filename: 'leak-rate-tracking.xlsx',
        sheets: [
            {
                name: 'Leak Rate Tracking',
                headers: LEAK_RATE_HEADERS,
                rows,
            },
        ],
    }
}

export function buildLeakRateTrackingCsv ():CsvOptions {
    return {
        headers: LEAK_RATE_HEADERS,
        rows: [],
        filename: 'leak-rate-tracking.csv',
    }
}

const REPAIR_RECORD_HEADERS:string[] = [
    'Date Leak Found',
    'Equipment ID',
    'Customer',
    'Refrigerant Type',
    'Leak Location',
    'Action Taken',
    '30-Day Deadline',
    'Repair Completed Date',
    'Technician',
    'Notes',
]

function repairRecordRow (rowNum:number):CellValue[] {
    const r = rowNum
    return [
        null,  // Date Leak Found (A)
        null,  // Equipment ID (B)
        null,  // Customer (C)
        null,  // Refrigerant Type (D)
        null,  // Leak Location (E)
        null,  // Action Taken (F)
        {      // 30-Day Deadline (G) = A + 30
            value: null,
            formula: `IF(A${r}<>"",A${r}+30,"")`,
        } as SpreadsheetCell,
        null,  // Repair Completed Date (H)
        null,  // Technician (I)
        null,  // Notes (J)
    ]
}

const EQUIPMENT_REGISTER_HEADERS:string[] = [
    'Equipment ID',
    'Customer',
    'Site Name',
    'Location',
    'Equipment Type',
    'Refrigerant Type',
    'Full System Charge (lbs)',
    'Appliance Type',
    'EPA Threshold %',
    'Install Date',
    'Last Service Date',
    'Notes',
]

const REFRIGERANT_INVENTORY_HEADERS:string[] = [
    'Date',
    'Refrigerant Type',
    'Purchased (lbs)',
    'Recovered (lbs)',
    'Current Inventory (lbs)',
    'Implied Usage (lbs)',
    'Supplier',
    'Invoice #',
    'Notes',
]

function inventoryRow (rowNum:number):CellValue[] {
    const r = rowNum
    return [
        null,  // Date (A)
        null,  // Refrigerant Type (B)
        null,  // Purchased (C)
        null,  // Recovered (D)
        null,  // Current Inventory (E)
        {      // Implied Usage = C - D - E
            value: null,
            formula: `IF(AND(C${r}<>"",D${r}<>"",E${r}<>""),C${r}-D${r}-E${r},"")`,
        } as SpreadsheetCell,
        null,  // Supplier (G)
        null,  // Invoice # (H)
        null,  // Notes (I)
    ]
}

export function buildBasicRefrigerantLogXlsx ():SpreadsheetOptions {
    const rows:null[][] = []
    for (let i = 0; i < 50; i++) {
        rows.push(REFRIGERANT_LOG_HEADERS.map(() => null))
    }
    return {
        filename: 'basic-refrigerant-log.xlsx',
        sheets: [
            {
                name: 'Refrigerant Log',
                headers: REFRIGERANT_LOG_HEADERS,
                rows,
            },
        ],
    }
}

export function buildBasicRefrigerantLogCsv ():CsvOptions {
    return {
        headers: REFRIGERANT_LOG_HEADERS,
        rows: [],
        filename: 'basic-refrigerant-log.csv',
    }
}

export function buildEpaComplianceWorkbookXlsx ():SpreadsheetOptions {
    const serviceLogRows:CellValue[][] = []
    for (let i = 2; i <= 51; i++) {
        serviceLogRows.push(leakRateRow(i))
    }

    const repairRows:CellValue[][] = []
    for (let i = 2; i <= 51; i++) {
        repairRows.push(repairRecordRow(i))
    }

    const equipmentRows:null[][] = []
    for (let i = 0; i < 50; i++) {
        equipmentRows.push(EQUIPMENT_REGISTER_HEADERS.map(() => null))
    }

    const inventoryRows:CellValue[][] = []
    for (let i = 2; i <= 51; i++) {
        inventoryRows.push(inventoryRow(i))
    }

    return {
        filename: 'epa-compliance-workbook.xlsx',
        sheets: [
            {
                name: 'Service Log',
                headers: LEAK_RATE_HEADERS,
                rows: serviceLogRows,
            },
            {
                name: 'Repair Record',
                headers: REPAIR_RECORD_HEADERS,
                rows: repairRows,
            },
            {
                name: 'Equipment Register',
                headers: EQUIPMENT_REGISTER_HEADERS,
                rows: equipmentRows,
            },
            {
                name: 'Refrigerant Inventory',
                headers: REFRIGERANT_INVENTORY_HEADERS,
                rows: inventoryRows,
            },
        ],
    }
}
